import React from 'react'
import LazyLoad from 'react-lazyload'
import { Link } from 'gatsby'
import { CtaLink } from 'components/design'
import ProjectAttributes from 'templates/portfolio/project-attributes'
import Rimg from './r-img'
import VideoWithCover from 'components/video-with-cover'
import VideoWithCoverHoverPlay from 'components/video-with-cover-hover-play'

export const ImageGrid = ({
	grid,
	keepImageHeight,
	publishedCaseStudies,
	isAnimationService,
	link1ToDisplay,
}) => (
	<section className="mt-6 grid grid-cols-1 gap-screen-step-1/2 px-screen-step-1/2 lg:mt-16 lg:grid-cols-2">
		{grid.map(({ component, items, verticalIndex, forceTwoColumns }, index) => {
			let item
			switch (component) {
				case 'single':
					item = (
						<Single
							key={items[0].image.url || items[0].video.url}
							items={items}
							keepImageHeight={keepImageHeight}
							forceTwoColumns={forceTwoColumns}
							publishedCaseStudies={publishedCaseStudies}
							isAnimationService={isAnimationService}
							linkToDisplay={
								index === 0 && link1ToDisplay ? link1ToDisplay : null
							}
						/>
					)
					break
				case 'double':
				case 'double-vertical':
					item = (
						<Double
							key={items[0].image.url || items[0].video.url}
							items={items}
							bothVertical={component.includes('vertical')}
							keepImageHeight={keepImageHeight}
							publishedCaseStudies={publishedCaseStudies}
							isAnimationService={isAnimationService}
						/>
					)
					break
				case 'triple':
					item = (
						<Triple
							key={items[0].image.url || items[0].image.url}
							items={items}
							verticalIndex={verticalIndex}
							keepImageHeight={keepImageHeight}
							publishedCaseStudies={publishedCaseStudies}
							isAnimationService={isAnimationService}
						/>
					)
					break
				case 'cta':
					item = <Cta key={items[0].image.url} items={items} />
					break
				default:
					item = null
			}
			return item
		})}
	</section>
)

const GridElement = ({
	image,
	position,
	mobilePosition,
	project,
	publishedCaseStudies,
	video,
	keepImageHeight,
	isAnimationService,
}) => {
	const caseStudyLink =
		project?.document?.uid &&
		publishedCaseStudies?.includes(project.document.uid) &&
		`/projects/${project.document.uid}/`.replace('--', '-')

	const LinkWrapper = ({ children }) =>
		caseStudyLink ? <Link to={caseStudyLink}>{children}</Link> : children

	const videoComponentProps = video?.url && {
		cover: image?.url ? image : null,
		coverPosition: position,
		mobileCoverPosition: mobilePosition,
		videoUrl: video.url,
	}

	return (
		<LazyLoad
			once
			offset={1200}
			className={
				`relative w-full ` +
				// TODO: simplify height dependency chain, as aspect-ratio does not work in iOS Safari
				(video?.url ? 'h-[93.75vw] ' : '') +
				(keepImageHeight ? '' : 'h-full lg:h-full')
			}
		>
			<LinkWrapper>
				{videoComponentProps ? (
					isAnimationService ? (
						<VideoWithCoverHoverPlay {...videoComponentProps} />
					) : (
						<VideoWithCover {...videoComponentProps} />
					)
				) : (
					<Rimg
						image={image}
						position={position}
						mobilePosition={mobilePosition}
						className={
							keepImageHeight ? `w-full` : `h-full w-full lg:object-cover`
						}
					/>
				)}
				<AtributesPopover project={project} />
			</LinkWrapper>
		</LazyLoad>
	)
}

const Single = ({
	items: [{ image, video, project }],
	keepImageHeight,
	forceTwoColumns,
	publishedCaseStudies,
	isAnimationService,
	linkToDisplay,
}) => {
	const LinkWrapper = ({ children }) =>
		linkToDisplay ? (
			<a href={linkToDisplay} target="_blank" rel="noreferrer">
				{children}
			</a>
		) : (
			children
		)

	return (
		<div
			className={[
				`group w-screen-step-15 lg:w-auto`,
				forceTwoColumns ? '' : 'lg:col-span-2',
				keepImageHeight ? '' : 'lg:h-[calc(17.25*var(--screen-grid-step))]',
			].join(' ')}
		>
			<LinkWrapper>
				<GridElement
					image={image}
					video={video}
					position={35}
					mobilePosition={15}
					project={project}
					keepImageHeight={keepImageHeight}
					publishedCaseStudies={publishedCaseStudies}
					isAnimationService={isAnimationService}
				/>
			</LinkWrapper>
		</div>
	)
}

const Double = ({
	items,
	bothVertical,
	keepImageHeight,
	publishedCaseStudies,
	isAnimationService,
}) => (
	<>
		{items.map(({ image, video, project }) => (
			<div
				key={image.url || video.url}
				className={`group w-screen-step-15 ${
					keepImageHeight
						? ''
						: bothVertical
						? 'lg:h-screen-step-35'
						: 'lg:h-[calc(17.25*var(--screen-grid-step))]'
				} lg:w-auto`}
			>
				<GridElement
					image={image}
					video={video}
					position={17.25}
					mobilePosition={15}
					project={project}
					keepImageHeight={keepImageHeight}
					publishedCaseStudies={publishedCaseStudies}
					isAnimationService={isAnimationService}
				/>
			</div>
		))}
	</>
)

const Triple = ({
	items,
	verticalIndex,
	keepImageHeight,
	publishedCaseStudies,
	isAnimationService,
}) => (
	<>
		{items.map(({ image, project, video }, index) => (
			<div
				key={image.url || video.url}
				className={`group w-screen-step-15 ${
					index === verticalIndex
						? 'lg:row-span-2'
						: keepImageHeight
						? ''
						: 'lg:h-[calc(17.25*var(--screen-grid-step))]'
				} lg:w-auto`}
			>
				<GridElement
					image={image}
					video={video}
					position={17.25}
					mobilePosition={15}
					project={project}
					keepImageHeight={keepImageHeight}
					publishedCaseStudies={publishedCaseStudies}
					isAnimationService={isAnimationService}
				/>
			</div>
		))}
	</>
)

const Cta = ({
	items: [{ first_line, second_line, button_link, button_text, image }],
}) => (
	<div className="h-screen-step-8 w-screen-step-15 lg:col-span-2 lg:h-screen-step-10 lg:w-auto">
		<LazyLoad once offset={1200} className="relative h-full w-full">
			<Rimg
				image={image}
				position={35}
				mobilePosition={15}
				className="h-full w-full object-cover"
			/>

			<div className="absolute top-1/2 left-screen-step -ml-screen-step-1/2 flex h-4/5 -translate-y-1/2 flex-col justify-between lg:left-screen-step-3 lg:h-2/5">
				<p className="text-base lg:text-xl">{first_line}</p>
				<h2 className="w-2/3 text-2xl lg:w-auto lg:text-4xl">{second_line}</h2>

				<CtaLink
					to={button_link}
					label={button_text}
					className="!w-screen-step-6 lg:!w-screen-step-4"
				/>
			</div>
		</LazyLoad>
	</div>
)

const AtributesPopover = ({ project }) =>
	project?.document ? (
		<div className="absolute bottom-screen-step-3/4 left-screen-step-3/4 overflow-hidden text-white">
			<div className="lg:translate-y-full lg:transition-transform lg:duration-300 lg:group-hover:translate-y-0">
				<ProjectAttributes project={project.document} />
			</div>
		</div>
	) : null
